/* =============================================================
   CountrySelectModal — shown when "Виз мэдүүлэх" is clicked
   Displays country cards; clicking navigates to /visa/:country
   ============================================================= */
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLocation } from "wouter";

const COUNTRIES = [
  { slug: "usa",       flag: "🇺🇸", name: "АНУ",       nameEn: "United States" },
  { slug: "australia", flag: "🇦🇺", name: "Австрали",   nameEn: "Australia"     },
  { slug: "canada",    flag: "🇨🇦", name: "Канад",      nameEn: "Canada"        },
  { slug: "ireland",   flag: "🇮🇪", name: "Ирланд",     nameEn: "Ireland"       },
  { slug: "czech",     flag: "🇨🇿", name: "Чех Улс",    nameEn: "Czech Republic"},
  { slug: "schengen",  flag: "🇪🇺", name: "Шенген",     nameEn: "Schengen Zone" },
  { slug: "new-zealand", flag: "🇳🇿", name: "Шинэ Зеланд", nameEn: "New Zealand"   },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function CountrySelectModal({ isOpen, onClose }: Props) {
  const [, navigate] = useLocation();

  const handleSelect = (slug: string) => {
    onClose();
    navigate(`/visa/${slug}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              className="relative w-full max-w-lg rounded-3xl overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #13103A 0%, #0F0B35 100%)",
                border: "1px solid rgba(124,58,237,0.25)",
                boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,58,237,0.1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4">
                <div>
                  <h2
                    className="text-white font-black text-xl"
                    style={{ fontFamily: "'Sora', var(--font-display), sans-serif", letterSpacing: "-0.02em" }}
                  >
                    Улсаа сонгоно уу
                  </h2>
                  <p className="text-white/40 text-sm mt-0.5" style={{ fontFamily: "var(--font-body)" }}>
                    Виз мэдүүлэх улсаа сонгоно уу
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
                  style={{ background: "rgba(255,255,255,0.06)" }}
                >
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>

              {/* Country grid */}
              <div className="grid grid-cols-2 gap-3 px-6 pb-6">
                {COUNTRIES.map((c, i) => (
                  <motion.button
                    key={c.slug}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleSelect(c.slug)}
                    className="group flex items-center gap-3 p-4 rounded-2xl text-left transition-all duration-200"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                      style={{
                        background: "rgba(124,58,237,0.12)",
                        border: "1px solid rgba(124,58,237,0.2)",
                      }}
                    >
                      {c.flag}
                    </div>
                    <div>
                      <div
                        className="text-white font-bold text-sm leading-tight"
                        style={{ fontFamily: "'Sora', var(--font-display), sans-serif" }}
                      >
                        {c.name}
                      </div>
                      <div className="text-white/35 text-[11px] mt-0.5" style={{ fontFamily: "var(--font-body)" }}>
                        {c.nameEn}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
